import { Dialog } from '@angular/cdk/dialog';
import { Overlay } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Injector,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { Appointment, MenuItem } from '@models';
import {
  ActiveTelehealthCallService,
  AppointmentsService,
  PROTOCOLS_API,
  ToastService,
  TreatmentProtocolsService,
} from '@services';
import { ConfirmDialogComponent, DropdownMenuComponent } from '@ui-lib';
import { catchError, EMPTY } from 'rxjs';
import { LabResultsDialogComponent } from '../../patient-list/patient-ai-lab/lab-results-dialog/lab-results-dialog.component';
import { PatientHandoutDialogComponent } from './patient-handout-dialog/patient-handout-dialog.component';
import { PatientProtocolDialogComponent } from '../../patient-list/patient-overview/patient-protocol-dialog/patient-protocol-dialog.component';

@Component({
  selector: 'provider-portal-telehealth-action-bar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'contents' },
  imports: [MatIconModule, DropdownMenuComponent],
  providers: [{ provide: PROTOCOLS_API, useClass: TreatmentProtocolsService }],
  templateUrl: './telehealth-action-bar.component.html',
})
export class TelehealthActionBarComponent {
  protected readonly call = inject(ActiveTelehealthCallService);
  private readonly dialog = inject(Dialog);
  private readonly injector = inject(Injector);
  private readonly overlay = inject(Overlay);
  private readonly protocolApi = inject(PROTOCOLS_API);
  private readonly appointmentsService = inject(AppointmentsService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly completeTriggerClass =
    'flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700';

  protected readonly completeEncounterMenu: MenuItem[][] = [
    [
      {
        id: 'successful-call',
        label: 'Successful call',
        icon: 'check-circle-outline',
        iconClass: '!text-gray-500',
      },
      {
        id: 'no-show',
        label: 'No show',
        icon: 'close-circle-outline',
        iconClass: '!text-gray-500',
      },
    ],
    [
      {
        id: 'cancel-appointment',
        label: 'Cancel appointment',
        icon: 'calendar-cancel-outline',
        iconClass: '!text-gray-500',
      },
    ],
  ];

  protected onCompleteEncounterAction(id: string): void {
    switch (id) {
      case 'successful-call':
        this.markSuccessful();
        break;
      case 'no-show':
        this.markNoShow();
        break;
      case 'cancel-appointment':
        this.cancelAppointment();
        break;
    }
  }

  protected openProtocols(): void {
    this.dialog.open<boolean>(PatientProtocolDialogComponent, {
      data: { initialSearch: '' },
      providers: [{ provide: PROTOCOLS_API, useValue: this.protocolApi }],
    });
  }

  protected openHandout(): void {
    const appointment = this.call.activeAppointment();
    if (!appointment) {
      return;
    }
    if (!appointment.chartNoteId) {
      this.toastService.showWarning(
        'Open the chart note for this appointment before creating a handout.'
      );
      return;
    }
    this.dialog.open(PatientHandoutDialogComponent, {
      data: {
        patientId: appointment.patientId,
        chartNoteId: appointment.chartNoteId,
        patientName: appointment.patientDisplayName,
      },
    });
  }

  protected openShareLabResults(): void {
    const appointment = this.call.activeAppointment();
    if (!appointment) {
      return;
    }
    this.dialog.open(LabResultsDialogComponent, {
      data: {
        patientId: appointment.patientId,
        patientName: appointment.patientDisplayName,
        mode: 'share',
      },
    });
  }

  private invoiceFlowLoading = false;

  protected async openCreateInvoice(): Promise<void> {
    const patientId = this.call.activeAppointment()?.patientId;
    if (!patientId || this.invoiceFlowLoading) {
      return;
    }

    this.invoiceFlowLoading = true;
    try {
      const { InvoiceFlowService } = await import(
        '../../../shared/services/invoice-flow.service'
      );
      this.injector.get(InvoiceFlowService).openInvoiceFlow({
        mode: 'Add',
        invoice: { patientId },
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
      });
    } catch {
      this.toastService.showWarning(
        'Failed to open the invoice form. Please try again.'
      );
    } finally {
      this.invoiceFlowLoading = false;
    }
  }

  protected markSuccessful(): void {
    const appointment = this.call.activeAppointment();
    if (!appointment) {
      return;
    }
    this.appointmentsService
      .markAsFulfilled({ appointmentId: appointment.id })
      .pipe(
        catchError(() => {
          this.toastService.showWarning(
            'Failed to complete the encounter. Please try again.'
          );
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.toastService.showSuccess('Encounter completed.');
        void this.call.end();
      });
  }

  protected markNoShow(): void {
    const appointment = this.call.activeAppointment();
    if (!appointment) {
      return;
    }
    this.appointmentsService
      .markAsNoshow({ appointmentId: appointment.id })
      .pipe(
        catchError(() => {
          this.toastService.showWarning(
            'Failed to mark the appointment as no-show. Please try again.'
          );
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.toastService.showSuccess('Appointment marked as no-show.');
        void this.call.end();
      });
  }

  protected cancelAppointment(): void {
    const appointment = this.call.activeAppointment();
    if (!appointment) {
      return;
    }
    this.dialog
      .open<boolean>(ConfirmDialogComponent, {
        data: {
          title: 'Do you want to cancel this appointment?',
          messageHtml: this.buildCancelMessage(appointment),
          confirmButtonLabel: 'Yes, cancel',
          confirmButtonColor: 'bg-red-700 hover:bg-red-800 focus:ring-red-300',
          onConfirm: () =>
            this.appointmentsService.cancelAppointment('provider', {
              appointmentId: appointment.id,
            }),
        },
      })
      .closed.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result) {
          this.toastService.showSuccess('Appointment cancelled.');
          void this.call.end();
        }
      });
  }

  private buildCancelMessage(appointment: Appointment): string {
    return `
      <p class="text-gray-500">
        You are about to cancel <strong>${appointment.appointmentTypeName} — ${appointment.patientDisplayName}</strong>. Please confirm if you wish to do it.
      </p>
    `;
  }
}
